/**
 * Utility for generating nationally recognized US holidays
 */

// Helper to format dates in ISO format
const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// Get nth weekday of a month (e.g., 3rd Monday of January)
const getNthWeekdayOfMonth = (year: number, month: number, weekday: number, n: number): Date => {
  let count = 0;
  let day = 1;
  const date = new Date(year, month, day);
  
  while (count < n) {
    date.setDate(day);
    if (date.getDay() === weekday) {
      count++;
    }
    if (count < n) {
      day++;
    }
  }
  
  return date;
};

// Get last weekday of a month (e.g., last Monday of May)
const getLastWeekdayOfMonth = (year: number, month: number, weekday: number): Date => {
  const date = new Date(year, month + 1, 0); // Last day of month
  const lastDay = date.getDate();
  
  let day = lastDay;
  while (date.getDay() !== weekday) {
    day--;
    date.setDate(day);
  }
  
  return date;
};

// Calculate Easter Sunday using Butcher's algorithm
const getEasterSunday = (year: number): Date => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  
  return new Date(year, month, day);
};

// Get Memorial Day (last Monday of May)
const getMemorialDay = (year: number): Date => {
  return getLastWeekdayOfMonth(year, 4, 1); // Last Monday (1) of May (4)
};

// Get Labor Day (first Monday of September)
const getLaborDay = (year: number): Date => {
  return getNthWeekdayOfMonth(year, 8, 1, 1); // First Monday (1) of September (8)
};

// Get Thanksgiving (fourth Thursday of November)
const getThanksgiving = (year: number): Date => {
  return getNthWeekdayOfMonth(year, 10, 4, 4); // Fourth Thursday (4) of November (10)
};

// Get Martin Luther King Jr. Day (third Monday of January)
const getMlkDay = (year: number): Date => {
  return getNthWeekdayOfMonth(year, 0, 1, 3); // Third Monday (1) of January (0)
};

// Get Presidents Day (third Monday of February)
const getPresidentsDay = (year: number): Date => {
  return getNthWeekdayOfMonth(year, 1, 1, 3); // Third Monday (1) of February (1)
};

// Get Columbus/Indigenous Peoples' Day (second Monday of October)
const getColumbusDay = (year: number): Date => {
  return getNthWeekdayOfMonth(year, 9, 1, 2); // Second Monday (1) of October (9)
};

// Get Veterans Day (November 11, or nearest weekday if on weekend)
const getVeteransDay = (year: number): Date => {
  const nov11 = new Date(year, 10, 11);
  const day = nov11.getDay();
  
  if (day === 0) { // Sunday, move to Monday
    return new Date(year, 10, 12);
  } else if (day === 6) { // Saturday, move to Friday
    return new Date(year, 10, 10);
  }
  return nov11;
};

// Get Juneteenth (June 19th, or nearest weekday if on weekend)
const getJuneteenth = (year: number): Date => {
  const june19 = new Date(year, 5, 19);
  const day = june19.getDay();
  
  if (day === 0) { // Sunday, move to Monday
    return new Date(year, 5, 20);
  } else if (day === 6) { // Saturday, move to Friday
    return new Date(year, 5, 18);
  }
  return june19;
};

// Generate list of US federal holidays for a given year
export interface Holiday {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  type: 'holiday';
  category?: 'federal' | 'cultural' | 'religious';
  color?: string;
}

export const getUSHolidays = (year: number): Holiday[] => {
  // New Year's Day
  const newYears = new Date(year, 0, 1);
  const newYearsObserved = new Date(year, 0, 1);
  if (newYears.getDay() === 0) { // Sunday, move to Monday
    newYearsObserved.setDate(2);
  } else if (newYears.getDay() === 6) { // Saturday, move to Friday
    newYearsObserved.setDate(newYears.getDate() - 1);
  }
  
  // Independence Day
  const independenceDay = new Date(year, 6, 4);
  const independenceDayObserved = new Date(year, 6, 4);
  if (independenceDay.getDay() === 0) { // Sunday, move to Monday
    independenceDayObserved.setDate(5);
  } else if (independenceDay.getDay() === 6) { // Saturday, move to Friday
    independenceDayObserved.setDate(3);
  }
  
  // Christmas Day
  const christmas = new Date(year, 11, 25);
  const christmasObserved = new Date(year, 11, 25);
  if (christmas.getDay() === 0) { // Sunday, move to Monday
    christmasObserved.setDate(26);
  } else if (christmas.getDay() === 6) { // Saturday, move to Friday
    christmasObserved.setDate(24);
  }
  
  // Easter related
  const easterSunday = getEasterSunday(year);
  const goodFriday = new Date(easterSunday);
  goodFriday.setDate(easterSunday.getDate() - 2);
  
  // Create holidays array
  const holidays: Holiday[] = [
    {
      id: `newyear-${year}`,
      title: "New Year's Day",
      start: newYearsObserved,
      end: newYearsObserved,
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a' // Light green
    },
    {
      id: `mlk-${year}`,
      title: "Martin Luther King Jr. Day",
      start: getMlkDay(year),
      end: getMlkDay(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `presidents-${year}`,
      title: "Presidents' Day",
      start: getPresidentsDay(year),
      end: getPresidentsDay(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `goodfriday-${year}`,
      title: "Good Friday",
      start: goodFriday,
      end: goodFriday,
      allDay: true,
      type: 'holiday',
      category: 'religious',
      color: '#9c27b0' // Purple
    },
    {
      id: `easter-${year}`,
      title: "Easter Sunday",
      start: easterSunday,
      end: easterSunday,
      allDay: true,
      type: 'holiday',
      category: 'religious',
      color: '#9c27b0'
    },
    {
      id: `memorial-${year}`,
      title: "Memorial Day",
      start: getMemorialDay(year),
      end: getMemorialDay(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `juneteenth-${year}`,
      title: "Juneteenth",
      start: getJuneteenth(year),
      end: getJuneteenth(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `independence-${year}`,
      title: "Independence Day",
      start: independenceDayObserved,
      end: independenceDayObserved,
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `labor-${year}`,
      title: "Labor Day",
      start: getLaborDay(year),
      end: getLaborDay(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `columbus-${year}`,
      title: "Columbus Day / Indigenous Peoples' Day",
      start: getColumbusDay(year),
      end: getColumbusDay(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `halloween-${year}`,
      title: "Halloween",
      start: new Date(year, 9, 31),
      end: new Date(year, 9, 31),
      allDay: true,
      type: 'holiday',
      category: 'cultural',
      color: '#ff9800' // Orange
    },
    {
      id: `veterans-${year}`,
      title: "Veterans Day",
      start: getVeteransDay(year),
      end: getVeteransDay(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `thanksgiving-${year}`,
      title: "Thanksgiving Day",
      start: getThanksgiving(year),
      end: getThanksgiving(year),
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `christmas-${year}`,
      title: "Christmas Day",
      start: christmasObserved,
      end: christmasObserved,
      allDay: true,
      type: 'holiday',
      category: 'federal',
      color: '#8bc34a'
    },
    {
      id: `newyeareve-${year}`,
      title: "New Year's Eve",
      start: new Date(year, 11, 31),
      end: new Date(year, 11, 31),
      allDay: true,
      type: 'holiday',
      category: 'cultural',
      color: '#ff9800'
    }
  ];
  
  return holidays;
};

// Generate multi-year holidays (useful for calendar views spanning multiple years)
export const getMultiYearHolidays = (startYear: number, endYear: number): Holiday[] => {
  let holidays: Holiday[] = [];
  
  for (let year = startYear; year <= endYear; year++) {
    holidays = [...holidays, ...getUSHolidays(year)];
  }
  
  return holidays;
};
